import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, Search, Bell, Sun, Moon, Wand2, LogIn, LogOut, ShieldCheck, User } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";

export default function Topbar({ onMenu }) {
  const { theme, cycle } = useTheme();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const segs = location.pathname.split("/").filter(Boolean);
  const crumb = segs.length ? segs[segs.length - 1].replace(/-/g, " ") : "dashboard";
  const isSuperAdmin = (user?.roles || []).includes("super_admin");
  const initials = user
    ? (user.name || user.email || "?")
        .split(/\s|@/)[0]
        .slice(0, 2)
        .toUpperCase()
    : "";

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const ThemeIcon = theme === "dark" ? Moon : theme === "creator" ? Wand2 : Sun;

  return (
    <header
      data-testid="app-topbar"
      className="sticky top-0 z-20 flex items-center gap-3 px-4 lg:px-6 h-16 border-b border-[var(--pl-border)]"
      style={{
        background: "var(--pl-sidebar)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
      }}
    >
      <button
        data-testid="topbar-menu-btn"
        onClick={onMenu}
        className="lg:hidden grid place-items-center h-9 w-9 rounded-lg hover:bg-white/5 text-[var(--pl-text)]"
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      <div className="hidden md:block">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-text-muted)]">
          PremierLab
        </div>
        <div className="font-display capitalize text-base font-medium text-[var(--pl-text)] -mt-0.5">
          {crumb}
        </div>
      </div>

      <div className="flex-1" />

      <div className="hidden md:flex items-center gap-2 px-3 h-10 rounded-xl pl-glass min-w-[280px]">
        <Search className="h-4 w-4 text-[var(--pl-text-muted)]" />
        <Input
          data-testid="topbar-search"
          placeholder="Search projects, templates, AI…"
          className="bg-transparent border-0 h-8 px-0 text-sm text-[var(--pl-text)] placeholder:text-[var(--pl-text-muted)] focus-visible:ring-0"
        />
        <kbd className="hidden lg:inline text-[10px] px-1.5 py-0.5 rounded font-mono border border-[var(--pl-border)] text-[var(--pl-text-muted)]">
          ⌘K
        </kbd>
      </div>

      <button
        data-testid="topbar-theme-toggle"
        onClick={cycle}
        className={cn(
          "grid place-items-center h-10 w-10 rounded-xl pl-glass text-[var(--pl-text)] hover:text-[var(--pl-aqua)] transition"
        )}
        aria-label="Cycle theme"
        title={`Theme: ${theme}`}
      >
        <ThemeIcon className="h-4 w-4" />
      </button>

      <button
        data-testid="topbar-notifications"
        className="relative grid place-items-center h-10 w-10 rounded-xl pl-glass text-[var(--pl-text)] hover:text-[var(--pl-aqua)] transition"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-[var(--pl-lime)]" />
      </button>

      <Link
        to="/create"
        data-testid="topbar-create-btn"
        className="hidden sm:inline-flex items-center gap-2 px-4 h-10 rounded-xl pl-btn-aqua text-sm"
      >
        <Wand2 className="h-4 w-4" />
        Create AI
      </Link>

      <div className="h-9 w-9 rounded-full pl-signature-gradient grid place-items-center text-[#061414] font-semibold text-xs ring-2 ring-[var(--pl-aqua)]/40 hidden">
        PL
      </div>

      {!user ? (
        <Link
          to="/login"
          data-testid="topbar-signin"
          className="inline-flex items-center gap-2 px-3.5 h-10 rounded-xl pl-btn-aqua text-sm font-semibold"
          title="Sign in"
        >
          <LogIn className="h-4 w-4" />
          <span className="hidden sm:inline">Sign in</span>
        </Link>
      ) : (
        <div ref={menuRef} className="relative">
          <button
            data-testid="topbar-account-btn"
            onClick={() => setMenuOpen((o) => !o)}
            className="h-10 pl-1 pr-3 inline-flex items-center gap-2 rounded-full pl-glass hover:bg-white/5 text-[var(--pl-text)]"
            aria-label="Account menu"
            aria-expanded={menuOpen}
          >
            <span className="h-8 w-8 rounded-full pl-signature-gradient grid place-items-center text-[#061414] font-bold text-[11px]">
              {initials}
            </span>
            <span className="hidden md:inline text-xs font-medium truncate max-w-[120px]">{user.name || user.email}</span>
          </button>
          {menuOpen && (
            <div
              data-testid="topbar-account-menu"
              className="absolute right-0 top-12 z-30 w-64 rounded-xl border border-[var(--pl-border)] backdrop-blur p-1.5"
              style={{
                background: "var(--pl-surface)",
                boxShadow: "0 24px 70px rgba(0,0,0,0.42)",
              }}
            >
              <div className="px-3 py-2 border-b border-[var(--pl-border)] mb-1">
                <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">Signed in</div>
                <div className="text-[13px] font-semibold text-[var(--pl-text)] truncate">{user.email}</div>
                {(user.roles || []).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(user.roles || []).map((r) => (
                      <span key={r} className="text-[9px] px-1.5 py-0.5 rounded-md font-mono bg-[var(--pl-aqua)]/12 text-[var(--pl-aqua)] border border-[var(--pl-aqua)]/30">
                        {r}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <MenuItem
                icon={User}
                label="Profile & Settings"
                onClick={() => { setMenuOpen(false); nav("/settings/profile"); }}
                testId="account-menu-profile"
              />
              {isSuperAdmin && (
                <MenuItem
                  icon={ShieldCheck}
                  label="Admin Console"
                  onClick={() => { setMenuOpen(false); nav("/admin"); }}
                  testId="account-menu-admin"
                />
              )}
              <div className="my-1 h-px bg-[var(--pl-border)]" />
              <MenuItem
                icon={LogOut}
                label="Sign out"
                onClick={() => { setMenuOpen(false); logout(); nav("/login"); }}
                testId="account-menu-signout"
                tone="danger"
              />
            </div>
          )}
        </div>
      )}
    </header>
  );
}

function MenuItem({ icon: I, label, onClick, testId, tone }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium text-left transition",
        tone === "danger"
          ? "text-rose-300 hover:bg-rose-500/10"
          : "text-[var(--pl-text)] hover:bg-[var(--pl-aqua)]/10 hover:text-[var(--pl-aqua)]"
      )}
    >
      <I className="h-4 w-4 opacity-80" />
      <span>{label}</span>
    </button>
  );
}
